import { useState, useEffect } from 'react';
import { Todo, TodoFormData, FilterOptions } from '../types/Todo';

const STORAGE_KEY = 'todo-app-todos';

export const useTodos = () => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [filteredTodos, setFilteredTodos] = useState<Todo[]>([]);
  const [filters, setFilters] = useState<FilterOptions>({
    status: 'all',
    priority: 'all',
    category: '',
    search: ''
  });

  // Load todos from localStorage on mount
  useEffect(() => {
    const savedTodos = localStorage.getItem(STORAGE_KEY);
    if (savedTodos) {
      try {
        setTodos(JSON.parse(savedTodos));
      } catch (error) {
        console.error('Error loading todos from localStorage:', error);
      }
    }
  }, []);

  // Save todos to localStorage whenever todos change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(todos));
  }, [todos]);

  // Filter todos based on current filters
  useEffect(() => {
    let filtered = todos;

    // Filter by status
    if (filters.status === 'completed') {
      filtered = filtered.filter(todo => todo.completed);
    } else if (filters.status === 'pending') {
      filtered = filtered.filter(todo => !todo.completed);
    }

    // Filter by priority
    if (filters.priority !== 'all') {
      filtered = filtered.filter(todo => todo.priority === filters.priority);
    }

    // Filter by category
    if (filters.category) {
      filtered = filtered.filter(todo => todo.category === filters.category);
    }

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(todo =>
        todo.title.toLowerCase().includes(searchLower) ||
        (todo.description && todo.description.toLowerCase().includes(searchLower))
      );
    }

    setFilteredTodos(filtered);
  }, [todos, filters]);

  const addTodo = (todoData: TodoFormData) => {
    const newTodo: Todo = {
      id: Date.now().toString(),
      ...todoData,
      completed: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setTodos(prev => [newTodo, ...prev]);
  };

  const updateTodo = (id: string, updates: Partial<TodoFormData>) => {
    setTodos(prev => prev.map(todo =>
      todo.id === id
        ? { ...todo, ...updates, updatedAt: new Date().toISOString() }
        : todo
    ));
  };

  const toggleTodo = (id: string) => {
    setTodos(prev => prev.map(todo =>
      todo.id === id
        ? { ...todo, completed: !todo.completed, updatedAt: new Date().toISOString() }
        : todo
    ));
  };

  const deleteTodo = (id: string) => {
    setTodos(prev => prev.filter(todo => todo.id !== id));
  };

  const clearCompleted = () => {
    setTodos(prev => prev.filter(todo => !todo.completed));
  };

  const clearAll = () => {
    setTodos([]);
  };

  const importTodos = (importedTodos: Todo[], mergeMode: boolean = true) => {
    if (mergeMode) {
      // Merge mode: add imported todos to existing ones
      setTodos(prev => {
        const existingIds = new Set(prev.map(todo => todo.id));
        const newTodos = importedTodos.filter(todo => !existingIds.has(todo.id));
        return [...prev, ...newTodos];
      });
    } else {
      // Replace mode: replace all existing todos
      setTodos(importedTodos);
    }
  };

  const getCategories = () => {
    const categories = new Set(todos.map(todo => todo.category));
    return Array.from(categories).sort();
  };

  const getStats = () => {
    const total = todos.length;
    const completed = todos.filter(todo => todo.completed).length;
    const pending = total - completed;
    const highPriority = todos.filter(todo => todo.priority === 'high' && !todo.completed).length;

    return { total, completed, pending, highPriority };
  };

  return {
    todos: filteredTodos,
    allTodos: todos,
    filters,
    setFilters,
    addTodo,
    updateTodo,
    toggleTodo,
    deleteTodo,
    clearCompleted,
    clearAll,
    importTodos,
    getCategories,
    getStats
  };
};
